import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { MapPinIcon } from "lucide-react";

/* ===== Leaflet additions ===== */
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

const pdfThumbnail = "/pdf_icon.png";
const API_BASE = process.env.REACT_APP_API_BASE || "";

L.Icon.Default.mergeOptions({ iconRetinaUrl, iconUrl, shadowUrl });

/* ====== HTTPS-safe URL helpers ====== */
const RAW_ORIGIN_FOR_UPLOADS =
  (process.env.REACT_APP_API_BASE && process.env.REACT_APP_API_BASE.replace(/\/+$/, "")) ||
  window.location.origin.replace(/\/+$/, "");

const ORIGIN_FOR_UPLOADS = RAW_ORIGIN_FOR_UPLOADS.startsWith("http://")
  ? RAW_ORIGIN_FOR_UPLOADS.replace("http://", "https://")
  : RAW_ORIGIN_FOR_UPLOADS;

const uploadUrlFromPath = (p) => {
  if (!p) return null;
  const s = String(p);
  if (s.startsWith("s3://") || /^https?:\/\//i.test(s) || s.startsWith("data:")) return null;
  const fname = s.split(/\\|\//).pop();
  if (!fname) return null;
  return `${ORIGIN_FOR_UPLOADS}/uploads/${encodeURIComponent(fname)}`;
};

const toFetchable = (url) => {
  if (!url) return null;
  try {
    const base = new URL(ORIGIN_FOR_UPLOADS);
    base.protocol = "https:";
    const u = new URL(url, base.href);
    if (u.protocol === "http:") u.protocol = "https:";
    const sameOrigin = u.origin === base.origin;
    if (sameOrigin) return u.href;
    return `${base.origin}/api/proxy-image?url=${encodeURIComponent(u.href)}`;
  } catch {
    return `${ORIGIN_FOR_UPLOADS}${url.startsWith("/") ? "" : "/"}${url}`;
  }
};

/* ====== S3 presign helpers ====== */
const s3KeyFromPath = (p) => {
  if (!p || !p.startsWith("s3://")) return null;
  const parts = p.split("/");
  if (parts.length < 4) return null;
  return parts.slice(3).join("/");
};

const presignReadUrl = async (key) => {
  const { data } = await axios.get(`${API_BASE}/api/s3/presign-read`, { params: { key } });
  return data?.url || null;
};

// ── Theme tokens ──────────────────────────────────────────────────
const teal      = "#40BEB4";
const gold      = "#FFC850";
const navy      = "#071520";
const cardBg    = "rgba(10,25,40,0.92)";
const border    = "rgba(64,190,180,0.2)";
const borderSub = "rgba(64,190,180,0.08)";

const inputStyle = {
  background: "rgba(255,255,255,0.05)",
  border: `1px solid ${border}`,
  borderRadius: "8px",
  color: "#fff",
  padding: "9px 12px",
  fontSize: "13px",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

const Dashboard = () => {
  const [kycData, setKycData]           = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate]   = useState("");
  const [endDate, setEndDate]       = useState("");

  const [viewRecord, setViewRecord] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [editForm, setEditForm]     = useState({});
  const [deleteId, setDeleteId]     = useState(null);
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(false);

  // ── FIX: responsive state instead of Tailwind/inline display conflict ──
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, { method: "POST", credentials: "include" });
    } catch (err) {
      console.warn("Logout request failed", err);
    }
    sessionStorage.removeItem("isAuthenticated");
    navigate("/login");
  };

  const recordScore = (r) => {
    const fields = [
      r.name, r.father, r.dob, r.mobile, r.address, r.state, r.pincode,
      r.fileUrl, r.candidatePhotoUrl, r.aadhaarPhotoFileUrl, r.aadhaarPhotoSourceUrl,
      r.latitude, r.longitude,
    ];
    const base = fields.reduce((acc, v) => acc + (v ? 1 : 0), 0);
    const serialBonus = Array.isArray(r.serialNumbers)
      ? Math.min(3, r.serialNumbers.filter(Boolean).length)
      : 0;
    return base + serialBonus;
  };

  const dedupeAndFilter = (list) => {
    const byVerif = new Map();
    for (const r of list || []) {
      const verif = r?.digilocker?.verificationId || r?._id;
      const existing = byVerif.get(verif);
      if (!existing) {
        byVerif.set(verif, r);
      } else {
        byVerif.set(verif, recordScore(r) >= recordScore(existing) ? r : existing);
      }
    }
    return Array.from(byVerif.values()).filter((r) => {
      const hasAnyUserField = r.name || r.father || r.dob || r.mobile || r.address || r.state || r.pincode;
      const hasAnyFile = r.fileUrl || r.candidatePhotoUrl || r.aadhaarPhotoFileUrl || r.aadhaarPhotoSourceUrl;
      return !!(hasAnyUserField || hasAnyFile);
    });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/kyc-data`);
      const cleaned = dedupeAndFilter(res.data || []);
      const withPresigned = await Promise.all(
        cleaned.map(async (r) => {
          const copy = { ...r };
          if (copy.filePath && String(copy.filePath).startsWith("s3://")) {
            const key = s3KeyFromPath(copy.filePath);
            copy.fileUrl = key ? await presignReadUrl(key) : null;
          }
          if (copy.candidatePhotoPath && String(copy.candidatePhotoPath).startsWith("s3://")) {
            const key = s3KeyFromPath(copy.candidatePhotoPath);
            copy.candidatePhotoUrl = key ? await presignReadUrl(key) : null;
          }
          if (copy.aadhaarPhotoPath && String(copy.aadhaarPhotoPath).startsWith("s3://")) {
            const key = s3KeyFromPath(copy.aadhaarPhotoPath);
            copy.aadhaarPhotoFileUrl = key ? await presignReadUrl(key) : null;
          }
          if (copy.trainingCertificatePath && String(copy.trainingCertificatePath).startsWith("s3://")) {
            const key = s3KeyFromPath(copy.trainingCertificatePath);
            copy.trainingCertificateUrl = key ? await presignReadUrl(key) : null;
          } else if (copy.trainingCertificatePath) {
            copy.trainingCertificateUrl = uploadUrlFromPath(copy.trainingCertificatePath) || null;
          }
          if (copy.extraDocumentPath && String(copy.extraDocumentPath).startsWith("s3://")) {
            const key = s3KeyFromPath(copy.extraDocumentPath);
            copy.extraDocumentUrl = key ? await presignReadUrl(key) : null;
          } else if (copy.extraDocumentPath) {
            copy.extraDocumentUrl = uploadUrlFromPath(copy.extraDocumentPath) || null;
          }
          return copy;
        })
      );
      setKycData(withPresigned);
      setFilteredData(withPresigned);
    } catch (err) {
      console.error("Error fetching data", err);
      setError("Failed to load records.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    let data = [...kycData];
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      data = data.filter((item) => {
        const serials = Array.isArray(item.serialNumbers)
          ? item.serialNumbers.join(" ").toLowerCase()
          : "";
        return (
          (item.name || "").toLowerCase().includes(lower) ||
          (item.mobile || "").toLowerCase().includes(lower) ||
          (getDigiMaskedUid(item) || "").toLowerCase().includes(lower) ||
          serials.includes(lower)
        );
      });
    }
    if (startDate || endDate) {
      const s = startDate ? new Date(startDate) : null;
      const e = endDate ? new Date(endDate) : null;
      data = data.filter((item) => {
        const d = item.createdAt ? new Date(item.createdAt) : null;
        if (!d || isNaN(d)) return false;
        return (!s || d >= s) && (!e || d <= e);
      });
    }
    setFilteredData(data);
  }, [searchTerm, startDate, endDate, kycData]);

  const formatDate = (d) => {
    if (!d) return "-";
    const date = new Date(d);
    return isNaN(date) ? d : date.toLocaleDateString();
  };

  const getDigi               = (rec) => rec?.digilocker || {};
  const getDigiStatus         = (rec) => getDigi(rec)?.status || "-";
  const getDigiVerificationId = (rec) => getDigi(rec)?.verificationId || "-";
  const getDigiMaskedUid      = (rec) =>
    getDigi(rec)?.document?.uid_masked ||
    getDigi(rec)?.userDetails?.uid_masked ||
    rec?.aadhaar || "-";

  const getCandidatePhotoUrl = (rec) => {
    const p = rec.candidatePhotoPath;
    const local = p && !String(p).startsWith("s3://") ? uploadUrlFromPath(p) : null;
    return local || (rec.candidatePhotoUrl ? toFetchable(rec.candidatePhotoUrl) : null);
  };
  const getAadhaarImageUrl = (rec) => {
    const p = rec.filePath;
    const local = p && !String(p).startsWith("s3://") ? uploadUrlFromPath(p) : null;
    return local || (rec.fileUrl ? toFetchable(rec.fileUrl) : null);
  };
  const getTrainingCertificateUrl = (rec) => {
    const p = rec.trainingCertificatePath;
    const local = p && !String(p).startsWith("s3://") ? uploadUrlFromPath(p) : null;
    return local || (rec.trainingCertificateUrl ? toFetchable(rec.trainingCertificateUrl) : null);
  };
  const getExtraDocumentUrl = (rec) => {
    const p = rec.extraDocumentPath;
    const local = p && !String(p).startsWith("s3://") ? uploadUrlFromPath(p) : null;
    return local || (rec.extraDocumentUrl ? toFetchable(rec.extraDocumentUrl) : null);
  };
  const getAadhaarPortraitUrl = (rec) => {
    const src = rec?.aadhaarPhotoFileUrl ? rec.aadhaarPhotoFileUrl : rec?.aadhaarPhotoSourceUrl || null;
    if (!src) return null;
    if (/^data:/i.test(src)) return src;
    if (!/^https?:\/\//i.test(src) && /^[A-Za-z0-9+/=]+$/.test(src.slice(0, 64)))
      return `data:image/jpeg;base64,${src}`;
    return toFetchable(src);
  };

  const isImageFile = (url = "") => {
    if (!url) return false;
    if (/^data:image\//i.test(url)) return true;
    return /\.(jpe?g|png|gif|webp|bmp)$/i.test(url.split("?")[0].toLowerCase());
  };

  const cleanCsv = (v) => {
    if (v == null) return "";
    const str = String(v).replace(/"/g, '""');
    return str.includes(",") || str.includes("\n") ? `"${str}"` : str;
  };

  const exportCSV = () => {
    const headers = [
      "Name","Mobile","Aadhaar(masked)","DOB","Father","Address","Pincode","State",
      "Latitude","Longitude","SerialNumbers","DL_Status","DL_VerificationId","DL_UID(masked)",
      "PurposeDetails","CarryArea","Convicted","AppliedBefore","LicenseSuspended",
      "FamilyHasLicence","SafePlaceToKeep","TrainingUnderRule10","TrainingCertificate","AdditionalDocument",
    ];
    const rows = filteredData.map((r) => {
      const op = r.otherParticulars || {};
      const trainUrl = getTrainingCertificateUrl(r) || r.trainingCertificateUrl || "";
      const extraUrl = getExtraDocumentUrl(r) || r.extraDocumentUrl || "";
      return [
        cleanCsv(r.name), cleanCsv(r.mobile), cleanCsv(r.aadhaar), cleanCsv(formatDate(r.dob)),
        cleanCsv(r.father), cleanCsv(r.address), cleanCsv(r.pincode), cleanCsv(r.state),
        cleanCsv(r.latitude), cleanCsv(r.longitude),
        cleanCsv(Array.isArray(r.serialNumbers) ? r.serialNumbers.join(" | ") : ""),
        cleanCsv(getDigiStatus(r)), cleanCsv(getDigiVerificationId(r)), cleanCsv(getDigiMaskedUid(r)),
        cleanCsv(op.purposeDetails || ""), cleanCsv(op.carryArea || ""),
        cleanCsv(op.convicted || ""), cleanCsv(op.appliedBefore || ""),
        cleanCsv(op.licenseSuspended || ""), cleanCsv(op.familyHasLicence || ""),
        cleanCsv(op.safePlaceToKeep || ""), cleanCsv(op.trainingUnderRule10 || ""),
        cleanCsv(trainUrl ? trainUrl.split("/").pop() || trainUrl : ""),
        cleanCsv(extraUrl ? extraUrl.split("/").pop() || extraUrl : ""),
      ].join(",");
    });
    const blob = new Blob([[headers.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "kyc_data.csv");
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("KYC Records", 14, 20);
    autoTable(doc, {
      startY: 30,
      head: [["Name","Mobile","Aadhaar(masked)","DOB","Father","Serial No(s)","Address","Pincode","State","DL Status"]],
      body: filteredData.map((r) => [
        r.name || "-", r.mobile || "-", getDigiMaskedUid(r), formatDate(r.dob), r.father || "-",
        (Array.isArray(r.serialNumbers) && r.serialNumbers.length) ? r.serialNumbers.join(", ") : "-",
        r.address || "-", r.pincode || "-", r.state || "-", getDigiStatus(r),
      ]),
      theme: "grid",
      styles: { fontSize: 8 },
      headStyles: { fillColor: [100, 100, 255] },
    });
    doc.save("kyc_data.pdf");
  };

  const urlToDataURL = async (url) => {
    if (/^data:image\//i.test(url)) {
      return { dataUrl: url, format: /data:image\/png/i.test(url) ? "PNG" : "JPEG" };
    }
    const safeUrl = toFetchable(url);
    const buildUrl = (u) => {
      const abs = new URL(u, ORIGIN_FOR_UPLOADS);
      abs.protocol = "https:";
      abs.searchParams.set("_cb", Date.now().toString());
      return abs.href;
    };
    const fetchAsBlob = async (u) =>
      (await axios.get(buildUrl(u), { responseType: "blob", withCredentials: false })).data;
    const blobToPngDataUrl = (blob) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        const objUrl = URL.createObjectURL(blob);
        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth || img.width || 0;
            canvas.height = img.naturalHeight || img.height || 0;
            canvas.getContext("2d").drawImage(img, 0, 0);
            URL.revokeObjectURL(objUrl);
            resolve({ dataUrl: canvas.toDataURL("image/png"), format: "PNG" });
          } catch (e) { URL.revokeObjectURL(objUrl); reject(e); }
        };
        img.onerror = (e) => { URL.revokeObjectURL(objUrl); reject(e); };
        img.src = objUrl;
      });
    try { return await blobToPngDataUrl(await fetchAsBlob(safeUrl)); }
    catch { return await blobToPngDataUrl(await fetchAsBlob(toFetchable(safeUrl))); }
  };

  const exportRecordPDF = async (rec) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFillColor(0, 102, 204);
    doc.rect(0, 0, pageWidth, 20, "F");
    doc.setFontSize(16); doc.setTextColor(255);
    doc.text("KYC Record", 14, 14);

    const boxX = 14, boxY = 40, boxSize = 50;
    doc.setDrawColor(0, 102, 204); doc.setLineWidth(1.5);
    doc.rect(boxX, boxY, boxSize, boxSize);

    const placeImage = (imgW, imgH, data, fmt, x, y) =>
      doc.addImage(data, fmt, x + (boxSize - imgW) / 2, y + (boxSize - imgH) / 2, imgW, imgH, undefined, "FAST");

    const photoUrl = getCandidatePhotoUrl(rec);
    if (photoUrl) {
      try {
        const { dataUrl, format } = await urlToDataURL(photoUrl);
        const tmp = new Image(); tmp.src = dataUrl;
        await new Promise((res) => (tmp.onload = res));
        const ratio = tmp.width / tmp.height;
        placeImage(ratio > 1 ? boxSize : boxSize * ratio, ratio > 1 ? boxSize / ratio : boxSize, dataUrl, format, boxX, boxY);
      } catch (e) { console.warn("PDF photo embed failed:", e?.message || e); }
    }

    const portraitBoxY = boxY + boxSize + 10;
    doc.rect(boxX, portraitBoxY, boxSize, boxSize);
    const portrait = getAadhaarPortraitUrl(rec);
    if (portrait) {
      try {
        const { dataUrl, format } = await urlToDataURL(portrait);
        const tmp2 = new Image(); tmp2.src = dataUrl;
        await new Promise((res) => (tmp2.onload = res));
        const r2 = tmp2.width / tmp2.height;
        placeImage(r2 > 1 ? boxSize : boxSize * r2, r2 > 1 ? boxSize / r2 : boxSize, dataUrl, format, boxX, portraitBoxY);
      } catch (e) { console.warn("PDF Aadhaar portrait failed:", e?.message || e); }
    }

    const docBoxY = portraitBoxY + boxSize + 10;
    doc.rect(boxX, docBoxY, boxSize, boxSize);
    const aadhaarDocUrl = getAadhaarImageUrl(rec) ? toFetchable(getAadhaarImageUrl(rec)) : null;
    if (aadhaarDocUrl) {
      try {
        const { dataUrl, format } = await urlToDataURL(aadhaarDocUrl);
        const tmp3 = new Image(); tmp3.src = dataUrl;
        await new Promise((res) => (tmp3.onload = res));
        const r3 = tmp3.width / tmp3.height;
        placeImage(r3 > 1 ? boxSize : boxSize * r3, r3 > 1 ? boxSize / r3 : boxSize, dataUrl, format, boxX, docBoxY);
      } catch (e) { console.warn("PDF Aadhaar doc failed:", e?.message || e); }
    }

    const other = rec.otherParticulars || {};
    autoTable(doc, {
      startY: boxY, margin: { left: boxX + boxSize + 10 },
      tableWidth: pageWidth - (boxX + boxSize + 10) - 14,
      theme: "grid", head: [["Field", "Details"]],
      body: [
        ["Name", rec.name || "-"], ["Mobile", rec.mobile || "-"],
        ["Aadhaar(masked)", getDigiMaskedUid(rec)], ["DOB", formatDate(rec.dob)],
        ["Father", rec.father || "-"],
        ["Serial No(s)", (Array.isArray(rec.serialNumbers) && rec.serialNumbers.length) ? rec.serialNumbers.join(", ") : "-"],
        ["Address", rec.address || "-"], ["Pincode", rec.pincode || "-"], ["State", rec.state || "-"],
        ["Latitude", rec.latitude || "-"], ["Longitude", rec.longitude || "-"],
        ["DL Status", getDigiStatus(rec)], ["DL Verification ID", getDigiVerificationId(rec)],
        ["Purpose(s) for licence", other.purposeDetails || "-"],
        ["Area applicant wishes to carry arms", other.carryArea || "-"],
        ["Convicted?", other.convicted || "-"], ["Applied before?", other.appliedBefore || "-"],
        ["License suspended?", other.licenseSuspended || "-"],
        ["Family has licence?", other.familyHasLicence || "-"],
        ["Safe place to keep?", other.safePlaceToKeep || "-"],
        ["Training under rule 10?", other.trainingUnderRule10 || "-"],
        ["Training Certificate", getTrainingCertificateUrl(rec) ? "Attached" : "-"],
        ["Additional Document", getExtraDocumentUrl(rec) ? "Attached" : "-"],
      ],
      styles: { fontSize: 10, cellPadding: 4, fillColor: [230, 240, 255], textColor: [0, 0, 0] },
      headStyles: { fillColor: [0, 102, 204], textColor: 255, fontStyle: "bold", halign: "center" },
      alternateRowStyles: { fillColor: [245, 250, 255] },
      didDrawCell: ({ section, cell }) => { if (section === "body") doc.roundedRect(cell.x, cell.y, cell.width, cell.height, 2, 2); },
    });

    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(10); doc.setTextColor(150);
    doc.text("Page 1", pageWidth / 2, pageHeight - 10, { align: "center" });
    doc.save(`kyc_${rec._id}.pdf`);
  };

  const openView  = (rec) => setViewRecord(rec);
  const closeView = () => setViewRecord(null);

  const openEdit = (rec) => {
    setEditRecord(rec);
    setEditForm({
      name: rec.name || "", dob: rec.dob || "", father: rec.father || "",
      address: rec.address || "", pincode: rec.pincode || "", state: rec.state || "",
      serialNumbers: Array.isArray(rec.serialNumbers) ? rec.serialNumbers : [],
    });
  };
  const closeEdit = () => { setEditRecord(null); setEditForm({}); };
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSerialChange = (index, value) => {
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setEditForm((prev) => {
      const updated = [...(prev.serialNumbers || [])];
      updated[index] = cleaned;
      return { ...prev, serialNumbers: updated };
    });
  };
  const addSerialNumber    = () =>
    setEditForm((prev) => ({ ...prev, serialNumbers: [...(prev.serialNumbers || []), ""] }));
  const removeSerialNumber = (index) =>
    setEditForm((prev) => {
      const updated = [...(prev.serialNumbers || [])];
      updated.splice(index, 1);
      return { ...prev, serialNumbers: updated };
    });

  const saveEdit = async () => {
    if (!editRecord) return;
    try {
      setSaving(true);
      const payload = {
        ...editForm,
        serialNumbers: Array.isArray(editForm.serialNumbers)
          ? editForm.serialNumbers.map((s) => String(s || "").trim().toUpperCase()).filter((s) => s.length > 0)
          : [],
      };
      await axios.put(`${API_BASE}/api/kyc-data/${editRecord._id}`, payload);
      await fetchData();
      closeEdit();
    } catch (err) {
      console.error("Error saving record", err);
      alert("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (id) => setDeleteId(id);
  const cancelDelete  = () => setDeleteId(null);
  const doDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleting(true);
      await axios.delete(`${API_BASE}/api/kyc-data/${deleteId}`);
      await fetchData();
      setDeleteId(null);
    } catch (err) {
      console.error("Error deleting record", err);
      alert("Failed to delete record");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => { document.title = "KYC Dashboard"; }, []);

  const hasValidLatLng = (rec) => {
    const lat = Number(rec?.latitude), lng = Number(rec?.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  };

  // ── Shared modal styles ────────────────────────────────────────
  const modalOverlay = {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)",
    zIndex: 40, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
  };
  const modalCard = {
    background: "linear-gradient(160deg, #0d1f2d 0%, #071520 100%)",
    border: `1px solid ${border}`, borderRadius: "18px",
    padding: "28px 28px 24px", position: "relative",
    width: "100%", maxWidth: "520px", maxHeight: "90vh", overflowY: "auto",
    boxShadow: "0 0 60px rgba(64,190,180,0.12), 0 24px 60px rgba(0,0,0,0.6)",
  };
  const closeBtn = (fn) => (
    <button onClick={fn} style={{
      position: "absolute", top: "14px", right: "16px",
      background: "none", border: "none", color: "rgba(255,255,255,0.4)",
      fontSize: "18px", cursor: "pointer", lineHeight: 1,
    }}>✕</button>
  );

  const td = (extra = {}) => ({
    padding: "10px 12px",
    borderBottom: `1px solid ${borderSub}`,
    color: "rgba(255,255,255,0.8)",
    whiteSpace: "nowrap",
    ...extra,
  });

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(160deg, #071520 0%, #0a1e2e 35%, #071018 100%)",
      position: "relative",
      overflow: "hidden",
      padding: "24px",
    }}>
      {/* Grid overlay */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: `
          linear-gradient(rgba(64,190,180,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(64,190,180,0.03) 1px, transparent 1px)
        `,
        backgroundSize: "48px 48px",
      }} />
      {/* Glow */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: `
          radial-gradient(ellipse at 25% 25%, rgba(64,190,180,0.06) 0%, transparent 55%),
          radial-gradient(ellipse at 75% 75%, rgba(255,200,80,0.05) 0%, transparent 55%)
        `,
      }} />

      <div style={{ position: "relative", zIndex: 10 }}>

        {/* ── Header ── */}
        <div style={{
          maxWidth: "1344px", margin: "0 auto 20px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <h1 style={{ fontSize: "22px", fontWeight: 600, color: teal, margin: 0 }}>
            KYC <span style={{ color: "#fff", fontWeight: 400 }}>Dashboard</span>
          </h1>
          <button
            onClick={handleLogout}
            style={{
              padding: "10px 24px",
              background: "linear-gradient(135deg, #1a8a82, #40BEB4)",
              border: "none", borderRadius: "8px", color: navy,
              fontSize: "13px", fontWeight: 700, letterSpacing: "1px",
              textTransform: "uppercase", cursor: "pointer",
              boxShadow: "0 4px 16px rgba(64,190,180,0.25)",
            }}
          >
            Logout
          </button>
        </div>

        {/* ── Filters ── */}
        <div style={{
          maxWidth: "1344px", margin: "0 auto 20px",
          background: cardBg, border: `1px solid ${border}`,
          borderRadius: "14px", padding: "16px 20px",
          display: "grid", gap: "12px",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          backdropFilter: "blur(12px)",
        }}>
          <input
            type="text"
            placeholder="Search name / mobile / masked UID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={inputStyle}
          />
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
          <input type="date" value={endDate}   onChange={(e) => setEndDate(e.target.value)}   style={inputStyle} />
          <button
            onClick={exportCSV}
            style={{
              padding: "9px 16px", background: "rgba(255,255,255,0.06)",
              border: `1px solid ${border}`, borderRadius: "8px",
              color: "rgba(255,255,255,0.8)", fontSize: "13px", fontWeight: 600, cursor: "pointer",
            }}
          >
            Export CSV
          </button>
          <button
            onClick={exportPDF}
            style={{
              padding: "9px 16px",
              background: "linear-gradient(135deg, #1a8a82, #40BEB4)",
              border: "none", borderRadius: "8px", color: navy,
              fontSize: "13px", fontWeight: 700, cursor: "pointer",
              boxShadow: "0 4px 14px rgba(64,190,180,0.25)",
            }}
          >
            Export PDF
          </button>
        </div>

        {/* ── Record count badge ── */}
        {!loading && filteredData.length > 0 && (
          <div style={{ maxWidth: "1344px", margin: "0 auto 12px" }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              background: "rgba(64,190,180,0.12)", border: `1px solid rgba(64,190,180,0.25)`,
              borderRadius: "20px", padding: "4px 14px", fontSize: "12px", color: teal,
            }}>
              <span style={{ fontWeight: 700 }}>{filteredData.length}</span> record{filteredData.length !== 1 ? "s" : ""} found
            </span>
          </div>
        )}

        {/* ── Table / Cards ── */}
        <div style={{ maxWidth: "1344px", margin: "0 auto" }}>
          {loading && (
            <div style={{ textAlign: "center", paddingTop: "60px" }}>
              <div style={{ color: teal, fontSize: "14px", marginBottom: "8px" }}>Loading records…</div>
              <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "12px" }}>Fetching from server</div>
            </div>
          )}
          {error   && <p style={{ color: "#ff6b7a", marginBottom: "16px" }}>{error}</p>}
          {!loading && filteredData.length === 0 && !error && (
            <div style={{
              textAlign: "center", paddingTop: "60px",
              color: "rgba(255,255,255,0.35)", fontSize: "14px",
            }}>
              <div style={{ fontSize: "32px", marginBottom: "12px" }}>📋</div>
              No KYC records found.
            </div>
          )}

          {/* ── FIX: use isMobile state, not Tailwind class + inline display:none conflict ── */}
          {!loading && filteredData.length > 0 && (
            <>
              {/* DESKTOP TABLE — shown when NOT mobile */}
              {!isMobile && (
                <div style={{
                  background: cardBg, border: `1px solid ${border}`,
                  borderRadius: "14px", padding: "16px", overflowX: "auto",
                  backdropFilter: "blur(12px)",
                }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ background: "rgba(64,190,180,0.1)" }}>
                        {["Name","Mobile","DOB","Serial No(s)","Address","GPS (Lat, Lng)","DigiLocker Status",
                          "Aadhaar(masked)","Aadhaar File","Candidate Photo","Aadhaar Photo",
                          "Training Cert","Additional Doc","Actions"].map((h) => (
                          <th key={h} style={{
                            padding: "10px 12px", color: teal, fontWeight: 600,
                            fontSize: "12px", textAlign: "left", whiteSpace: "nowrap",
                            borderBottom: `1px solid ${border}`,
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((item) => (
                        <tr
                          key={item._id}
                          style={{ transition: "background 0.15s" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(64,190,180,0.05)")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <td style={td()}>{item.name || "-"}</td>
                          <td style={td()}>{item.mobile || "-"}</td>
                          <td style={td()}>{formatDate(item.dob)}</td>
                          <td style={td()}>
                            {item.serialNumbers && item.serialNumbers.length > 0 ? item.serialNumbers.join(", ") : "-"}
                          </td>
                          <td style={td({ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" })} title={item.address}>
                            {item.address || "-"}
                          </td>

                          {/* GPS */}
                          <td style={td({ textAlign: "center" })}>
                            {item.latitude && item.longitude ? (
                              <div>
                                <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "12px", display: "block" }}>
                                  {`${Number(item.latitude).toFixed(5)}, ${Number(item.longitude).toFixed(5)}`}
                                </span>
                                <a
                                  href={`https://www.google.com/maps?q=${item.latitude},${item.longitude}`}
                                  target="_blank" rel="noopener noreferrer"
                                  style={{ color: teal, fontSize: "12px", textDecoration: "underline", display: "inline-flex", alignItems: "center", gap: "3px" }}
                                >
                                  <MapPinIcon size={12} style={{ color: "#ff6b7a" }} /> View on Map
                                </a>
                              </div>
                            ) : (
                              <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "12px" }}>No location</span>
                            )}
                          </td>

                          {/* DigiLocker status */}
                          <td style={td()}>
                            <span style={{
                              color: getDigiStatus(item) === "AUTHENTICATED" || getDigiStatus(item) === "SUCCESS"
                                ? "#4ade80"
                                : getDigiStatus(item) === "EXPIRED" || getDigiStatus(item) === "CONSENT_DENIED"
                                ? "#ff6b7a"
                                : "rgba(255,255,255,0.6)",
                              fontWeight: 600, fontSize: "12px",
                            }}>
                              {getDigiStatus(item)}
                            </span>
                          </td>

                          <td style={td()}>{getDigiMaskedUid(item)}</td>

                          {/* Aadhaar File */}
                          <td style={td({ textAlign: "center" })}>
                            {(() => {
                              const url = getAadhaarImageUrl(item);
                              if (!url) return <span style={{ color: "rgba(255,255,255,0.3)" }}>-</span>;
                              return (
                                <div style={{ width: "40px", height: "40px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <img src={url} alt="Aadhaar"
                                    style={{ height: "40px", width: "40px", objectFit: "cover", borderRadius: "6px", border: `1px solid ${border}` }}
                                    onError={(e) => { e.currentTarget.style.display = "none"; const n = e.currentTarget.nextElementSibling; if (n) n.style.display = "block"; }}
                                  />
                                  <img src={pdfThumbnail} alt="PDF" style={{ display: "none", height: "40px", width: "40px", objectFit: "contain" }} />
                                </div>
                              );
                            })()}
                          </td>

                          {/* Candidate Photo */}
                          <td style={td({ textAlign: "center" })}>
                            {getCandidatePhotoUrl(item)
                              ? <img src={getCandidatePhotoUrl(item)} alt="Candidate"
                                  style={{ height: "40px", width: "40px", objectFit: "cover", borderRadius: "50%", border: `1px solid ${border}`, display: "block", margin: "0 auto" }}
                                  onError={(e) => (e.currentTarget.style.display = "none")} />
                              : <span style={{ color: "rgba(255,255,255,0.3)" }}>-</span>}
                          </td>

                          {/* Aadhaar Photo */}
                          <td style={td({ textAlign: "center" })}>
                            {(() => {
                              const portrait = getAadhaarPortraitUrl(item);
                              if (!portrait) return <span style={{ color: "rgba(255,255,255,0.3)" }}>-</span>;
                              return <img src={portrait} alt="Aadhaar Photo"
                                style={{ height: "40px", width: "40px", objectFit: "cover", borderRadius: "50%", border: `1px solid ${border}`, display: "block", margin: "0 auto" }}
                                onError={(e) => (e.currentTarget.style.display = "none")} />;
                            })()}
                          </td>

                          {/* Training Certificate */}
                          <td style={td({ textAlign: "center" })}>
                            {getTrainingCertificateUrl(item) ? (
                              isImageFile(getTrainingCertificateUrl(item))
                                ? <img src={getTrainingCertificateUrl(item)} alt="Training"
                                    style={{ height: "40px", width: "40px", objectFit: "cover", borderRadius: "6px", border: `1px solid ${border}`, display: "block", margin: "0 auto" }}
                                    onError={(e) => (e.currentTarget.style.display = "none")} />
                                : <a href={getTrainingCertificateUrl(item)} target="_blank" rel="noopener noreferrer"
                                    style={{ color: teal, fontSize: "12px", textDecoration: "underline" }}>View</a>
                            ) : <span style={{ color: "rgba(255,255,255,0.3)" }}>-</span>}
                          </td>

                          {/* Additional Doc */}
                          <td style={td({ textAlign: "center" })}>
                            {getExtraDocumentUrl(item) ? (
                              isImageFile(getExtraDocumentUrl(item))
                                ? <img src={getExtraDocumentUrl(item)} alt="Extra"
                                    style={{ height: "40px", width: "40px", objectFit: "cover", borderRadius: "6px", border: `1px solid ${border}`, display: "block", margin: "0 auto" }}
                                    onError={(e) => (e.currentTarget.style.display = "none")} />
                                : <a href={getExtraDocumentUrl(item)} target="_blank" rel="noopener noreferrer"
                                    style={{ color: teal, fontSize: "12px", textDecoration: "underline" }}>View</a>
                            ) : <span style={{ color: "rgba(255,255,255,0.3)" }}>-</span>}
                          </td>

                          {/* Actions */}
                          <td style={td()}>
                            <div style={{ display: "flex", gap: "6px" }}>
                              <button onClick={() => openView(item)} style={{ padding: "5px 10px", fontSize: "11px", background: "rgba(64,190,180,0.15)", border: `1px solid rgba(64,190,180,0.3)`, borderRadius: "6px", color: teal, cursor: "pointer", fontWeight: 600 }}>Read More</button>
                              <button onClick={() => openEdit(item)} style={{ padding: "5px 10px", fontSize: "11px", background: "rgba(255,200,80,0.12)", border: "1px solid rgba(255,200,80,0.3)", borderRadius: "6px", color: gold, cursor: "pointer", fontWeight: 600 }}>Edit</button>
                              <button onClick={() => confirmDelete(item._id)} style={{ padding: "5px 10px", fontSize: "11px", background: "rgba(255,107,122,0.12)", border: "1px solid rgba(255,107,122,0.3)", borderRadius: "6px", color: "#ff6b7a", cursor: "pointer", fontWeight: 600 }}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* MOBILE CARDS — shown only on mobile */}
              {isMobile && (
                <div style={{ display: "flex", gap: "12px", flexDirection: "column" }}>
                  {filteredData.map((item) => (
                    <div key={item._id} style={{
                      background: cardBg, border: `1px solid ${border}`,
                      borderRadius: "12px", padding: "16px",
                      color: "rgba(255,255,255,0.8)", fontSize: "13px",
                    }}>
                      {[
                        ["Name", item.name],
                        ["Mobile", item.mobile],
                        ["Aadhaar(masked)", getDigiMaskedUid(item)],
                        ["DOB", formatDate(item.dob)],
                        ["Serial No(s)", item.serialNumbers && item.serialNumbers.length > 0 ? item.serialNumbers.join(", ") : "-"],
                        ["Address", item.address],
                        ["DigiLocker Status", getDigiStatus(item)],
                      ].map(([label, value]) => (
                        <p key={label} style={{ margin: "4px 0" }}>
                          <span style={{ color: teal, fontWeight: 600 }}>{label}:</span> {value || "-"}
                        </p>
                      ))}
                      <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                        <button onClick={() => openView(item)} style={{ padding: "6px 12px", fontSize: "12px", background: "rgba(64,190,180,0.15)", border: `1px solid rgba(64,190,180,0.3)`, borderRadius: "6px", color: teal, cursor: "pointer", fontWeight: 600 }}>Read More</button>
                        <button onClick={() => openEdit(item)} style={{ padding: "6px 12px", fontSize: "12px", background: "rgba(255,200,80,0.12)", border: "1px solid rgba(255,200,80,0.3)", borderRadius: "6px", color: gold, cursor: "pointer", fontWeight: 600 }}>Edit</button>
                        <button onClick={() => confirmDelete(item._id)} style={{ padding: "6px 12px", fontSize: "12px", background: "rgba(255,107,122,0.12)", border: "1px solid rgba(255,107,122,0.3)", borderRadius: "6px", color: "#ff6b7a", cursor: "pointer", fontWeight: 600 }}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── READ MORE MODAL ── */}
        {viewRecord && (
          <div style={modalOverlay}>
            <div style={{ ...modalCard, maxWidth: "580px" }}>
              {closeBtn(closeView)}
              <div style={{
                position: "absolute", inset: 0, pointerEvents: "none",
                backgroundImage: "url('/advitiya_trans.png')", backgroundRepeat: "no-repeat",
                backgroundPosition: "center", backgroundSize: "200px auto", opacity: 0.06,
              }} />
              <h2 style={{ fontSize: "18px", fontWeight: 600, color: teal, marginBottom: "16px" }}>KYC Details</h2>
              {getCandidatePhotoUrl(viewRecord) && (
                <div style={{ textAlign: "center", marginBottom: "16px" }}>
                  <img src={getCandidatePhotoUrl(viewRecord)} alt="Candidate"
                    style={{ width: "96px", height: "96px", objectFit: "cover", borderRadius: "50%", border: `2px solid ${border}`, display: "block", margin: "0 auto" }}
                    onError={(e) => (e.currentTarget.style.display = "none")} />
                  <p style={{ fontSize: "12px", color: "rgba(255,255,255,0.4)", marginTop: "6px" }}>Candidate Photo</p>
                </div>
              )}
              <table style={{ width: "100%", fontSize: "13px", borderCollapse: "collapse", marginBottom: "16px" }}>
                <tbody>
                  {[
                    ["Name", viewRecord.name],
                    ["Mobile", viewRecord.mobile],
                    ["Aadhaar(masked)", getDigiMaskedUid(viewRecord)],
                    ["Serial Number(s)", viewRecord.serialNumbers && viewRecord.serialNumbers.length > 0 ? viewRecord.serialNumbers.join(", ") : "-"],
                    ["DOB", formatDate(viewRecord.dob)],
                    ["Father", viewRecord.father],
                    ["Address", viewRecord.address],
                    ["Pincode", viewRecord.pincode],
                    ["State", viewRecord.state],
                    ["DigiLocker Status", getDigiStatus(viewRecord)],
                    ["DL Verification ID", getDigiVerificationId(viewRecord)],
                  ].map(([label, value]) => (
                    <tr key={label}>
                      <td style={{ padding: "8px 12px", color: "rgba(255,255,255,0.45)", background: "rgba(255,255,255,0.03)", fontWeight: 500, borderBottom: `1px solid ${borderSub}`, whiteSpace: "nowrap", width: "40%" }}>{label}</td>
                      <td style={{ padding: "8px 12px", color: "rgba(255,255,255,0.85)", borderBottom: `1px solid ${borderSub}` }}>{value || "-"}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ padding: "8px 12px", color: "rgba(255,255,255,0.45)", background: "rgba(255,255,255,0.03)", fontWeight: 500, borderBottom: `1px solid ${borderSub}` }}>GPS (Lat, Lng)</td>
                    <td style={{ padding: "8px 12px", color: "rgba(255,255,255,0.85)", borderBottom: `1px solid ${borderSub}` }}>
                      {viewRecord.latitude && viewRecord.longitude
                        ? `${Number(viewRecord.latitude).toFixed(5)}, ${Number(viewRecord.longitude).toFixed(5)}`
                        : "N/A"}
                      {viewRecord.latitude && viewRecord.longitude && (
                        <a href={`https://www.google.com/maps?q=${viewRecord.latitude},${viewRecord.longitude}`}
                          target="_blank" rel="noopener noreferrer"
                          style={{ color: teal, textDecoration: "underline", fontSize: "12px", marginLeft: "8px", display: "inline-flex", alignItems: "center", gap: "3px" }}>
                          <MapPinIcon size={12} style={{ color: "#ff6b7a" }} /> Open in Google Maps
                        </a>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>

              {hasValidLatLng(viewRecord) && (
                <div style={{ marginBottom: "16px" }}>
                  <p style={{ fontSize: "11px", fontWeight: 600, color: "rgba(255,255,255,0.4)", marginBottom: "8px", letterSpacing: "1px", textTransform: "uppercase" }}>Location Preview</p>
                  <div style={{ borderRadius: "10px", overflow: "hidden", border: `1px solid ${border}`, height: "240px" }}>
                    <MapContainer center={[Number(viewRecord.latitude), Number(viewRecord.longitude)]} zoom={16}
                      style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
                      <Marker position={[Number(viewRecord.latitude), Number(viewRecord.longitude)]} />
                    </MapContainer>
                  </div>
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
                {(() => {
                  const url = getAadhaarImageUrl(viewRecord);
                  if (!url) return null;
                  return (
                    <div>
                      <p style={{ fontSize: "11px", fontWeight: 600, color: "rgba(255,255,255,0.4)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "1px" }}>Aadhaar File</p>
                      <img src={url} alt="Aadhaar"
                        style={{ maxHeight: "160px", width: "auto", borderRadius: "8px", border: `1px solid ${border}` }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      <div style={{ marginTop: "8px" }}>
                        <a href={url} target="_blank" rel="noreferrer" style={{ color: teal, textDecoration: "underline", fontSize: "12px" }}>Open Document</a>
                      </div>
                    </div>
                  );
                })()}
                {(() => {
                  const portrait = getAadhaarPortraitUrl(viewRecord);
                  if (!portrait) return null;
                  return (
                    <div>
                      <p style={{ fontSize: "11px", fontWeight: 600, color: "rgba(255,255,255,0.4)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "1px" }}>Aadhaar Photo</p>
                      <img src={portrait} alt="Aadhaar Photo"
                        style={{ maxHeight: "160px", width: "auto", borderRadius: "8px", border: `1px solid ${border}`, objectFit: "cover" }}
                        onError={(e) => (e.currentTarget.style.display = "none")} />
                    </div>
                  );
                })()}
              </div>

              <div>
                <p style={{ fontSize: "13px", fontWeight: 600, color: "rgba(255,255,255,0.7)", marginBottom: "10px" }}>Other particulars</p>
                {viewRecord.otherParticulars ? (
                  <div style={{ display: "grid", gap: "8px", fontSize: "13px" }}>
                    {[
                      ["Convicted", "convicted"],
                      ["Applied before", "appliedBefore"],
                      ["License suspended/cancelled/revoked", "licenseSuspended"],
                      ["Family has licence", "familyHasLicence"],
                      ["Safe place to keep arms", "safePlaceToKeep"],
                      ["Training under rule 10", "trainingUnderRule10"],
                      ["Purpose(s)", "purposeDetails"],
                      ["Area to carry arms", "carryArea"],
                    ].map(([label, key]) => (
                      <div key={key} style={{ display: "flex", gap: "8px" }}>
                        <div style={{ width: "200px", color: "rgba(255,255,255,0.4)", flexShrink: 0 }}>{label}</div>
                        <div style={{ color: "rgba(255,255,255,0.8)", fontWeight: 500 }}>{String(viewRecord.otherParticulars[key] ?? "-")}</div>
                      </div>
                    ))}
                    <div style={{ display: "flex", gap: "8px" }}>
                      <div style={{ width: "200px", color: "rgba(255,255,255,0.4)", flexShrink: 0 }}>Training Certificate</div>
                      <div>
                        {getTrainingCertificateUrl(viewRecord)
                          ? <a href={getTrainingCertificateUrl(viewRecord)} target="_blank" rel="noreferrer" style={{ color: teal, textDecoration: "underline" }}>
                              {isImageFile(getTrainingCertificateUrl(viewRecord)) ? "Open Image" : "Open Document"}
                            </a>
                          : <span style={{ color: "rgba(255,255,255,0.4)" }}>-</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <div style={{ width: "200px", color: "rgba(255,255,255,0.4)", flexShrink: 0 }}>Additional Document</div>
                      <div>
                        {getExtraDocumentUrl(viewRecord)
                          ? <a href={getExtraDocumentUrl(viewRecord)} target="_blank" rel="noreferrer" style={{ color: teal, textDecoration: "underline" }}>
                              {isImageFile(getExtraDocumentUrl(viewRecord)) ? "Open Image" : "Open Document"}
                            </a>
                          : <span style={{ color: "rgba(255,255,255,0.4)" }}>-</span>}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.35)" }}>No additional particulars recorded.</p>
                )}
              </div>

              <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={() => exportRecordPDF(viewRecord)}
                  style={{
                    padding: "10px 24px",
                    background: "linear-gradient(135deg,#7c3aed,#9f67fa)",
                    border: "none", borderRadius: "8px", color: "#fff",
                    fontSize: "13px", fontWeight: 700, cursor: "pointer",
                    boxShadow: "0 4px 16px rgba(124,58,237,0.3)",
                  }}
                >
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── EDIT MODAL ── */}
        {editRecord && (
          <div style={modalOverlay}>
            <div style={modalCard}>
              {closeBtn(closeEdit)}
              <h2 style={{ fontSize: "18px", fontWeight: 600, color: gold, marginBottom: "20px" }}>Edit KYC Record</h2>
              <div style={{ display: "grid", gap: "14px" }}>
                {[
                  ["Name", "name", "text"], ["DOB", "dob", "date"], ["Father", "father", "text"],
                  ["Address", "address", "text"], ["Pincode", "pincode", "text"], ["State", "state", "text"],
                ].map(([label, name, type]) => (
                  <div key={name}>
                    <label style={{ display: "block", fontSize: "11px", fontWeight: 500, color: "rgba(255,255,255,0.45)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: "6px" }}>{label}</label>
                    <input type={type} name={name} value={editForm[name] || ""} onChange={handleEditChange} style={inputStyle} />
                  </div>
                ))}
              </div>
              <div style={{ marginTop: "16px" }}>
                <label style={{ display: "block", fontSize: "11px", fontWeight: 500, color: "rgba(255,255,255,0.45)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: "8px" }}>Serial Number(s)</label>
                {(editForm.serialNumbers || []).map((sn, idx) => (
                  <div key={idx} style={{ display: "flex", gap: "8px", marginBottom: "8px", alignItems: "center" }}>
                    <input
                      type="text" value={sn}
                      onChange={(e) => handleSerialChange(idx, e.target.value)}
                      placeholder="Enter alphanumeric serial"
                      style={{ ...inputStyle, flex: 1, textTransform: "uppercase" }}
                    />
                    <button type="button" onClick={() => removeSerialNumber(idx)}
                      style={{ background: "none", border: "none", color: "#ff6b7a", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addSerialNumber}
                  style={{ background: "none", border: "none", color: teal, fontSize: "13px", fontWeight: 600, cursor: "pointer", marginTop: "4px" }}>
                  + Add Serial Number
                </button>
                <p style={{ fontSize: "11px", color: "rgba(255,255,255,0.3)", marginTop: "6px" }}>Only A–Z and 0–9 allowed.</p>
              </div>
              <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button onClick={closeEdit} style={{ padding: "10px 20px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "8px", color: "rgba(255,255,255,0.6)", fontSize: "13px", cursor: "pointer" }}>Cancel</button>
                <button onClick={saveEdit} disabled={saving} style={{ padding: "10px 24px", background: "linear-gradient(135deg,#1a8a82,#40BEB4)", border: "none", borderRadius: "8px", color: navy, fontSize: "13px", fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── DELETE MODAL ── */}
        {deleteId && (
          <div style={modalOverlay}>
            <div style={{ ...modalCard, maxWidth: "360px", textAlign: "center" }}>
              {closeBtn(cancelDelete)}
              <div style={{ fontSize: "32px", marginBottom: "12px" }}>🗑️</div>
              <h2 style={{ fontSize: "18px", fontWeight: 600, color: "#ff6b7a", marginBottom: "8px" }}>Confirm Delete</h2>
              <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.5)", marginBottom: "24px" }}>
                Are you sure you want to delete this record? This cannot be undone.
              </p>
              <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
                <button onClick={cancelDelete} style={{ padding: "10px 24px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "8px", color: "rgba(255,255,255,0.6)", fontSize: "13px", cursor: "pointer" }}>Cancel</button>
                <button onClick={doDelete} disabled={deleting} style={{ padding: "10px 24px", background: "linear-gradient(135deg,#b91c1c,#ef4444)", border: "none", borderRadius: "8px", color: "#fff", fontSize: "13px", fontWeight: 700, cursor: deleting ? "not-allowed" : "pointer", opacity: deleting ? 0.6 : 1 }}>
                  {deleting ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Dashboard;
